/**
 * @author: subhasish.ghosh@zoom.us
 */

import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { Box, Tooltip, Typography, Link, Dialog, DialogTitle, DialogContent, IconButton, Divider } from "@mui/material";
import classNames from "classnames";
import moment from "moment";
import "react-resizable/css/styles.css";
import { DATE_FORMAT } from "@utils/timeUnits";
import {
  getFormattedAmount,
  isSelectedActionTypeBizOperationDirect,
  isSelectedActionTypeTNS,
  isSelectedActionTypeNonPaymentDirect,
  isSelectedActionTypeNonPaymentIndirect,
  getFormattedAmountNonDecimalCurrency,
  isNonDecimalCurrency,
  getProductCatalogueStatus,
  isCurrencyNotAvailable,
  getTnsReasonDescription,
} from "@utils/utils";
import { STATUS_CLASS_MAP, PROCESSING_STATUS_CLASS_MAP } from "@utils/constants"
import "@components/styles.css";
import React, { useState, useMemo } from "react";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import flags from "@flags";
import { DISABLE_DECLINE_CANCELLATION_ACTION_TYPES } from '@utils/constants'
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from '@mui/icons-material/Info';
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

export const hColumnsDashboard = (selectedActionType) => {
  const isNonPaymentDirect =
    isSelectedActionTypeNonPaymentDirect(selectedActionType);
  const isNonPaymentIndirect =
    isSelectedActionTypeNonPaymentIndirect(selectedActionType);
  const isBizOperationDirect =
    isSelectedActionTypeBizOperationDirect(selectedActionType);
  const isTNS = isSelectedActionTypeTNS(selectedActionType);

  return [
    {
      field: "dwaCreatedDate",
      headerName: "Submission Date",
      width: 110,
      align: "center",
      headerAlign: "center",
      filterable: false,
      sortable: false,
      renderHeader: () => (
        <HeaderWithFooterCell
          header={"Submission Date"}
          footer={DATE_FORMAT}
          tooltip={"Submission Date"}
        />
      ),
      renderCell: (params) => {
        const raw = params?.row?.dwaUpdatedDate;
        return raw ? moment(raw).format(DATE_FORMAT) : "";
      },
    },
    {
      field: "accountNumber",
      headerName: "Account Number",
      width: 130,
      align: "left",
      headerAlign: "center",
      filterable: false,
      renderCell: (params) => (
        <HyperlinkCell
          linkText={params?.row?.accountNumber}
          linkUrl={params?.row?.accountUrl}
          copyable
          copyLabel="Account Number"
        />
      ),
    },
    {
      field: "name",
      headerName: "Account Name",
      width: 100,
      align: "left",
      headerAlign: "center",
      filterable: false,
      renderCell: (params) => (
        <CopyableTextCell
          value={params?.row?.name}
          label="Account Name"
        />
      ),
    },
    {
      field: "ticketNumber",
      headerName: "Ticket Number",
      width: 100,
      align: "left",
      headerAlign: "center",
      filterable: false,
    },
    {
      field: "zoomAccountNumber",
      headerName: "ZAN",
      description: "Zoom Account Number",
      width: 140,
      align: "right",
      headerAlign: "center",
      filterable: false,
      cellClassName: "age-grid-button",
      renderHeader: (params) => (
        <HeaderWithFooterCell
          header={"Zoom Account Number"}
          tooltip={"Zoom Account Number"}
        />
      ),
      renderCell: (params) => (
        <CopyableTextCell
          value={params?.row?.zoomAccountNumber}
          label="Zoom Account Number"
          align="flex-end"
        />
      ),
    },
    ...(isNonPaymentIndirect
      ? [
          {
            field: "affectedAccountCount",
            headerName: "Affected Account Count",
            description: "Affected Account Count",
            width: 100,
            align: "right",
            headerAlign: "center",
            filterable: false,
            cellClassName: "age-grid-button",
          renderHeader: (params) => (
              <HeaderWithFooterCell
                header={"Affected Account Count"}
                tooltip={"Affected Account Count"}
              />
            ),
          },
        ]
      : []),
    {
      field: "industry",
      headerName: "Industry",
      width: 100,
      align: "left",
      headerAlign: "center",
      filterable: false,
    },
    {
      field: "accountOwnerDivision",
      headerName: "Account Owner Division",
      width: 150,
      align: "left",
      headerAlign: "center",
      filterable: false,
    },
    ...(isTNS
      ? [
        {
          field: "ownerName",
          headerName: "Account Owner Name",
          width: 150,
          align: "left",
          headerAlign: "center",
          filterable: false,
          renderCell: (params) => (
            <CopyableTextCell
              value={params?.row?.ownerName}
              label="Account Owner Name"
            />
          ),
        },
        {
          field: "accountOwnerManagerName",
          headerName: "Account Owner Manager",
          width: 150,
          align: "left",
          headerAlign: "center",
          filterable: false,
        },
      ]
      : []),
    ...(DISABLE_DECLINE_CANCELLATION_ACTION_TYPES.includes(
      selectedActionType) || isBizOperationDirect
      ? [
        {
          field: "subscriptionName",
          headerName: "Subscription Name",
          width: 150,
          align: "left",
          headerAlign: "center",
          filterable: false,
          renderCell: (params) => {
            const subId = params?.row?.subId;
            const subscriptionName = params?.row?.subName;

            if (!subId) {
              return <span>{subscriptionName}</span>;
            }

            const zuoraHost = params?.row?.zuoraHost;
            const baseUrl = zuoraHost;
            const url = `${baseUrl}/platform/subscriptions/${subId}`;
            return (
              <HyperlinkCell
                linkText={subscriptionName}
                linkUrl={url}
              />
            );
          },
        },
          {
            field: "subscriptionTermEndDate",
            headerName: "Subscription Term End Date",
            width: 150,
            align: "center",
            headerAlign: "center",
            filterable: false,
            renderHeader: () => (
              <HeaderWithFooterCell
                header={"Subscription Term End Date"}
                footer={DATE_FORMAT}
                tooltip={"Subscription Term End Date"}
              />
            ),
            renderCell: (params) => {
              const raw = params?.row?.subTermEndDate;
              return raw ? moment(raw).format(DATE_FORMAT) : "";
            },
          },
        ]
      : []),
    ...(isNonPaymentDirect || isBizOperationDirect || isTNS
      ? [
          {
            field: isTNS ? "reasonCode" : "cancellationReason",
            headerName: "Cancellation Reason",
            width: 120,
            align: "left",
            headerAlign: "center",
            filterable: false,
            sortable: false,
            cellClassName: "age-grid-button",
            renderCell: (params) => (
              <CancellationReasonCell
                value={
                  isTNS
                    ? params?.row?.reasonCode
                    : params?.row?.cancellationReason
                }
                isTNS={isTNS}
              />
            ),
          },
        ]
      : []),
    {
      field: "segment",
      headerName: "Segment",
      width: 140,
      align: "left",
      headerAlign: "center",
      filterable: false,
    },
    {
      field: "salesChannelDisplayName",
      headerName: "Sales Channel",
      width: 110,
      align: "left",
      headerAlign: "center",
      sortable: false,
      filterable: false,
    },
    {
      field: "mrrUSD",
      headerName: "Committed MRR",
      description: "Committed MRR",
      width: 120,
      align: "right",
      headerAlign: "center",
      filterable: false,
      renderHeader: (params) => (
        <HeaderWithFooterCell
          header={"Committed MRR"}
          tooltip={"Committed MRR"}
        />
      ),
      renderCell: (params) => (
        <BalanceCell
          balance={params?.row?.subCmrr}
          balanceUSD={params?.row?.mrrUSD}
          currency={params?.row?.currency || ""}
        />
      ),
    },
    {
      field: "accountBalanceUSD",
      headerName: "Balance",
      width: 120,
      align: "right",
      headerAlign: "center",
      filterable: false,
      renderHeader: (params) => (
        <Tooltip title="Balance">
          <div className="table-header-amount">
            <strong className="table-header-ellipsis">Balance</strong>
          </div>
        </Tooltip>
      ),
      renderCell: (params) => (
        <BalanceCell
          balance={params?.row?.accountBalance}
          balanceUSD={params?.row?.accountBalanceUSD}
          currency={params?.row?.currency || ""}
        />
      ),
    },
    {
      field: "currency",
      headerName: "Currency",
      width: 100,
      align: "left",
      headerAlign: "center",
      filterable: false,
      renderCell: (params) => (
        <CurrencyCell localCurrency={params?.row?.currency || ""} />
      ),
    },
    {
      field: "subOriginalCreatedDate",
      headerName: "Subscription Creation Date",
      width: 120,
      align: "left",
      headerAlign: "center",
      filterable: false,
      sortable: false,
      cellClassName: "age-grid-button",
      renderHeader: (params) => (
        <HeaderWithFooterCell
          header={"Subscription Creation Date"}
          footer={DATE_FORMAT}
          tooltip={"Subscription Creation Date"}
        />
      ),
    },
    {
      field: "level_1_approver",
      headerName: "Level 1 Approver",
      width: 175,
      align: "left",
      headerAlign: "center",
      filterable: false,
      sortable: false,
      renderCell: (params) => (
        <ApprovalStatusHistorical
          approvalRejectionDate={params?.row?.level_1_statusDate}
          promisedPayDate={params?.row?.promisedPayDate}
          showPromisedPayDate={isNonPaymentDirect}
          names={params?.row?.level_1_approver}
          status={params?.row?.level_1_status}
        />
      ),
    },
    {
      field: "level_2_approver",
      headerName: "Level 2 Approver",
      width: 175,
      align: "left",
      headerAlign: "center",
      filterable: false,
      sortable: false,
      renderCell: (params) => (
        <ApprovalStatusHistorical
          approvalRejectionDate={params?.row?.level_2_statusDate}
          promisedPayDate={params?.row?.promisedPayDate}
          showPromisedPayDate={isNonPaymentDirect}
          names={params?.row?.level_2_approver}
          status={params?.row?.level_2_status}
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 140,
      headerAlign: "left",
      align: "center",
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        const val = params?.row?.status || " ";
        const cls = STATUS_CLASS_MAP[val] || "pending_status";

        return (
          <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <Typography className={cls} sx={{ textAlign: "center" }}>
              {val}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "processingStatus",
      headerName: "Processing Status",
      width: 160,
      headerAlign: "left",
      align: "center",
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        const val = params?.row?.processingStatus || " ";
        const cls = PROCESSING_STATUS_CLASS_MAP[val] || "pending_status";

        return (
          <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <Typography className={cls} sx={{ textAlign: "center" }}>
              {val}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "errorMessage",
      headerName: "Error Message",
      width: 220,
      headerAlign: "center",
      align: "left",
      filterable: false,
      sortable: false,
      renderCell: (params) => <ErrorMessageCell value={params?.row?.errorMessage} />,
    },
    {
      field: "businessComment",
      headerName: "Business Reason",
      width: 200,
      align: "left",
      headerAlign: "center",
      sortable: false,
      filterable: false,
    },
    ...(isNonPaymentDirect || isBizOperationDirect
      ? [
          {
            field: "detailedInfo",
            headerName: "Detailed Info",
            width: 150,
            align: "left",
            headerAlign: "center",
            filterable: false,
            sortable: false,
            cellClassName: "age-grid-button",
          },
        ]
      : []),
    ...(DISABLE_DECLINE_CANCELLATION_ACTION_TYPES.includes(
      selectedActionType)
      ? [
          {
            field: "deferCancellation",
            headerName: "Defer Cancellation",
            width: 300,
            align: "left",
            headerAlign: "center",
            filterable: false,
            sortable: false,
            renderCell: (params) => (
              <DeferCancellationHistory audits={params?.row?.cancellationAudit} />
            ),
          },
        ]
      : []),
  ];
};

export const columnsDashboard = (selectedActionType) => {
  const isNonPaymentDirect =
    isSelectedActionTypeNonPaymentDirect(selectedActionType);
  const isNonPaymentIndirect =
    isSelectedActionTypeNonPaymentIndirect(selectedActionType);
  const isBizOperationDirect =
    isSelectedActionTypeBizOperationDirect(selectedActionType);
  const isTNS = isSelectedActionTypeTNS(selectedActionType);

  return [
    {
      field: "accountNumber",
      headerName: "Account Number",
      width: 150,
      align: "left",
      headerAlign: "center",
      filterable: false,
      renderCell: (params) => (
        <HyperlinkCell
          linkText={params?.row?.accountNumber}
          linkUrl={params?.row?.accountUrl}
          copyable
          copyLabel="Account Number"
        />
      ),
    },
    {
      field: "name",
      headerName: "Account Name",
      width: 140,
      align: "left",
      headerAlign: "center",
      filterable: false,
      renderCell: (params) => (
        <CopyableTextCell
          value={params?.row?.name}
          label="Account Name"
        />
      ),
    },
    {
      field: "ticketNumber",
      headerName: "Ticket Number",
      width: 120,
      align: "left",
      headerAlign: "center",
      filterable: false,
    },
    {
      field: "executionDate", // backend request to pass executionDate for soting instead of dueIn
      headerName: "Due for Cancel (days)",
      width: 120,
      headerAlign: "center",
      align: "center",
      filterable: false,
      sortable: true,
      cellClassName: "age-grid-button",
      renderHeader: (params) => (
        <HeaderWithFooterCell
          header={"Due for Cancel"}
          footer={"in days"}
          tooltip={"Due for Cancel"}
        />
      ),
      renderCell: (params) => <AgeCell value={params?.row?.dueIn} />,
    },
    {
      field: "zoomAccountNumber",
      headerName: "ZAN",
      description: "Zoom Account Number",
      width: 150,
      align: "right",
      headerAlign: "center",
      filterable: false,
      cellClassName: "age-grid-button",
      renderHeader: (params) => (
        <HeaderWithFooterCell
          header={"Zoom Account Number"}
          tooltip={"Zoom Account Number"}
        />
      ),
      renderCell: (params) => (
        <CopyableTextCell
          value={params?.row?.zoomAccountNumber}
          label="Zoom Account Number"
          align="flex-end"
        />
      ),
    },
    ...(isNonPaymentIndirect
      ? [
          {
            field: "affectedAccountCount",
            headerName: "Affected Account Count",
            description: "Affected Account Count",
            width: 140,
            align: "right",
            headerAlign: "center",
            filterable: false,
            cellClassName: "age-grid-button",
          renderHeader: (params) => (
              <HeaderWithFooterCell
                header={"Affected Account Count"}
                tooltip={"Affected Account Count"}
              />
            ),
          },
        ]
      : []),
    {
      field: "industry",
      headerName: "Industry",
      width: 130,
      align: "left",
      headerAlign: "center",
      filterable: false,
    },
    {
      field: "accountOwnerDivision",
      headerName: "Account Owner Division",
      width: 150,
      align: "left",
      headerAlign: "center",
      filterable: false,
    },
    ...(isTNS
      ? [
        {
          field: "ownerName",
          headerName: "Account Owner Name",
          width: 150,
          align: "left",
          headerAlign: "center",
          filterable: false,
          renderCell: (params) => (
            <CopyableTextCell
              value={params?.row?.ownerName}
              label="Account Owner Name"
            />
          ),
        },
        {
          field: "accountOwnerManagerName",
          headerName: "Account Owner Manager",
          width: 150,
          align: "left",
          headerAlign: "center",
          filterable: false,
        },
      ]
      : []),
    ...(DISABLE_DECLINE_CANCELLATION_ACTION_TYPES.includes(
      selectedActionType) || isBizOperationDirect
      ? [
        {
          field: "subscriptionName",
          headerName: "Subscription Name",
          width: 150,
          align: "left",
          headerAlign: "center",
          filterable: false,
          renderCell: (params) => {
            const subId = params?.row?.subId;
            const subscriptionName = params?.row?.subName;

            if (!subId) {
              return <span>{subscriptionName}</span>;
            }

            const zuoraHost = params?.row?.zuoraHost;
            const baseUrl = zuoraHost || ZUORA_BASE_URL;
            const url = `${baseUrl}/platform/subscriptions/${subId}`;
            return (
              <HyperlinkCell
                linkText={subscriptionName}
                linkUrl={url}
              />
            );
          },
        },
        {
          field: "subscriptionTermEndDate",
          headerName: "Subscription Term End Date",
          width: 150,
          align: "center",
          headerAlign: "center",
          filterable: false,
          renderHeader: () => (
            <HeaderWithFooterCell
              header={"Subscription Term End Date"}
              footer={DATE_FORMAT}
              tooltip={"Subscription Term End Date"}
            />
          ),
          renderCell: (params) => {
            const raw = params?.row?.subTermEndDate;
            const formatted = raw ? moment(raw).format(DATE_FORMAT) : "";
            const dueIn = params?.row?.dueIn;
            const today = moment().startOf('day');
            const termDate = raw ? moment(raw).startOf('day') : null;
            // Determine if tooltip should be shown
            let showTooltip = false;
            if (selectedActionType === 'nonRenewalDirect') {
              // Always show for Non-Renewal Direct
             showTooltip = !!raw;;
            } else if (isBizOperationDirect) {
              // Show for Billing Ops only if date <= today
              showTooltip = !!raw && termDate.isSameOrBefore(today);
            }
            return (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                <span>{formatted}</span>
                {showTooltip && (
                  <Tooltip
                    title={`Subscription has reached the end of its term and is scheduled for cancellation in ${dueIn} day(s).`}
                    arrow
                  >
                    <InfoIcon
                      fontSize="15px"
                      sx={{ color: "action.active", cursor: "help" }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          },
        }
        ]
      : []),
    ...(isNonPaymentDirect || isBizOperationDirect || isTNS
      ? [
          {
            field: isTNS ? "reasonCode" : "cancellationReason",
            headerName: "Cancellation Reason",
            width: 130,
            align: "left",
            headerAlign: "center",
            filterable: false,
            sortable: false,
            cellClassName: "age-grid-button",
          renderHeader: (params) => (
              <HeaderWithFooterCell
                header={"Cancellation Reason"}
                tooltip={"Cancellation Reason"}
              />
            ),
            renderCell: (params) => (
              <CancellationReasonCell
                value={
                  isTNS
                    ? params?.row?.reasonCode
                    : params?.row?.cancellationReason
                }
                isTNS={isTNS}
              />
            ),
          },
        ]
      : []),
    {
      field: "segment",
      headerName: "Segment",
      width: 140,
      align: "left",
      headerAlign: "center",
      filterable: false,
    },
    {
      field: "salesChannelDisplayName",
      headerName: "Sales Channel",
      width: 140,
      align: "left",
      headerAlign: "center",
      sortable: false,
      filterable: false,
    },
    {
      field: "mrrUSD",
      headerName: "Committed MRR",
      description: "Committed MRR",
      width: 100,
      align: "right",
      headerAlign: "center",
      filterable: false,
      renderHeader: (params) => (
        <HeaderWithFooterCell
          header={"Committed MRR"}
          tooltip={"Committed MRR"}
        />
      ),
      renderCell: (params) => (
        <BalanceCell
          balance={params?.row?.subCmrr}
          balanceUSD={params?.row?.mrrUSD}
          currency={params?.row?.currency || ""}
        />
      ),
    },
    {
      field: "accountBalanceUSD",
      headerName: "Balance",
      width: 100,
      align: "right",
      headerAlign: "center",
      filterable: false,
      renderHeader: (params) => (
        <Tooltip title="Balance">
          <div className="table-header-amount">
            <strong className="table-header-ellipsis">Balance</strong>
          </div>
        </Tooltip>
      ),
      renderCell: (params) => (
        <BalanceCell
          balance={params?.row?.accountBalance}
          balanceUSD={params?.row?.accountBalanceUSD}
          currency={params?.row?.currency || ""}
        />
      ),
    },
    {
      field: "currency",
      headerName: "Currency",
      width: 90,
      align: "left",
      headerAlign: "center",
      filterable: false,
      renderCell: (params) => (
        <CurrencyCell localCurrency={params?.row?.currency || ""} />
      ),
    },
    {
      field: "subOriginalCreatedDate",
      headerName: "Subscription Creation Date",
      width: 120,
      align: "left",
      headerAlign: "center",
      filterable: false,
      sortable: false,
      cellClassName: "age-grid-button",
      renderHeader: (params) => (
        <HeaderWithFooterCell
          header={"Subscription Creation Date"}
          footer={DATE_FORMAT}
          tooltip={"Subscription Creation Date"}
        />
      ),
    },
    {
      field: "level_1_approver",
      headerName: "Level 1 Approver",
      width: 164,
      align: "left",
      headerAlign: "center",
      filterable: false,
      sortable: false,
      renderCell: (params) => (
        <ApprovalStatus
          approvalRejectionDate={params?.row?.level_1_statusDate}
          promisedPayDate={params?.row?.promisedPayDate}
          showPromisedPayDate={isNonPaymentDirect}
          names={params?.row?.level_1_approver}
          status={params?.row?.level_1_status}
        />
      ),
    },
    {
      field: "level_2_approver",
      headerName: "Level 2 Approver",
      width: 164,
      align: "left",
      headerAlign: "center",
      filterable: false,
      sortable: false,
      renderCell: (params) => (
        <ApprovalStatus
          approvalRejectionDate={params?.row?.level_2_statusDate}
          promisedPayDate={params?.row?.promisedPayDate}
          showPromisedPayDate={isNonPaymentDirect}
          names={params?.row?.level_2_approver}
          status={params?.row?.level_2_status}
        />
      ),
    },
    {
      field: "processingStatus",
      headerName: "Processing Status",
      width: 160,
      headerAlign: "left",
      align: "center",
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        const val = params?.row?.processingStatus || " ";
        const cls = PROCESSING_STATUS_CLASS_MAP[val] || "pending_status";

        return (
          <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <Typography className={cls} sx={{ textAlign: "center" }}>
              {val}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "errorMessage",
      headerName: "Error Message",
      width: 220,
      headerAlign: "center",
      align: "left",
      filterable: false,
      sortable: false,
      renderCell: (params) => (
        <ErrorMessageCell value={params?.row?.errorMessage} />
      ),
    },
    {
      field: "businessComment",
      headerName: "Business Reason",
      width: 200,
      align: "left",
      headerAlign: "center",
      sortable: false,
      filterable: false,
    },
    ...(isNonPaymentDirect || isBizOperationDirect
      ? [
          {
            field: "detailedInfo",
            headerName: "Detailed Info",
            width: 150,
            align: "left",
            headerAlign: "center",
            filterable: false,
            sortable: false,
            cellClassName: "age-grid-button",
          },
        ]
      : []),
  ];
};

export const columnsIndustry = (selectedActionType) => {
  const isTNS = isSelectedActionTypeTNS(selectedActionType);

  return [
    {
      field: "id",
      headerName: "Industry",
      width: 100,
      align: "left",
      headerAlign: "center",
      sortable: false,
      filterable: false,
    },
    ...(isTNS
      ? [
          {
            field: "reasonCode",
            headerName: "Cancellation Reason",
            width: 200,
            align: "left",
            headerAlign: "center",
            filterable: false,
            sortable: false,
            cellClassName: "age-grid-button",
            renderCell: (params) => (
              <CancellationReasonCell value={params?.row?.reasonCode} isTNS />
            ),
          },
        ]
      : []),
    {
      field: "count",
      headerName: "Count",
      width: 150,
      align: "right",
      headerAlign: "center",
      sortable: false,
      filterable: false,
    },
  ];
};

export const columnsApprovalRejectionModal = (
  selectedActionType,
  isApproveAction,
  isExecutionDecline = false,
  onExtendedDateChange,
  isDeferBlockedByExtension = false
) => {
  // Columns set for Decline Extension flow
  if (isExecutionDecline) {
    return [
      {
        field: "accountNumber",
        headerName: "Account Number",
        width: 125,
        headerAlign: "center",
        align: "left",
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <HyperlinkCell
            linkText={params?.row?.accountNumber}
            linkUrl={params?.row?.accountUrl}
          />
        ),
      },
      {
        field: "name",
        headerName: "Account Name",
        width: 170,
        headerAlign: "center",
        align: "left",
        sortable: false,
        filterable: false,
      },
      {
        field: "dueIn",
        headerName: "Due for Cancel (days)",
        width: 130,
        headerAlign: "center",
        align: "center",
        sortable: false,
        filterable: false,
        cellClassName: "age-grid-button",
        renderHeader: () => (
          <HeaderWithFooterCell
            header={"Due for Cancel"}
            footer={"in days"}
            tooltip={"Due for Cancel"}
          />
        ),
        renderCell: (params) => <AgeCell value={params?.row?.dueIn} />,
      },
      {
        field: "executionDate",
        headerName: "Execution Date",
        width: 140,
        headerAlign: "center",
        align: "center",
        sortable: false,
        filterable: false,
        cellClassName: "age-grid-button",
        renderHeader: () => (
          <HeaderWithFooterCell
            header={"Execution Date"}
            footer={DATE_FORMAT}
            tooltip={"Execution Date"}
          />
        ),
        renderCell: (params) => {
          const raw = params?.row?.executionDate;
          return raw ? moment(raw).format(DATE_FORMAT) : "—";
        },
      },
      {
        field: "extendedExecutionDate",
        headerName: "Extend Upto",
        width: 190,
        headerAlign: "center",
        align: "center",
        sortable: false,
        filterable: false,
        cellClassName: "age-grid-button",
        renderHeader: () => (
          <HeaderWithFooterCell
            header={"Extend Upto"}
            footer={DATE_FORMAT}
            tooltip={"Pick a new execution date"}
          />
        ),
        renderCell: (params) => {
          const { row } = params;

          const exec = row?.executionDate ? dayjs(row.executionDate) : null;
          const min = exec ? exec.add(1, "day") : null;
          const max = exec ? exec.add(60, "day") : null;

          // read-only when there is a cancellationExtensionDate already syncing
          const isReadOnlyExtend =
            isExecutionDecline &&
            isDeferBlockedByExtension &&
            !!row.cancellationExtensionDate;

          // choose which date to display in the input
          const rawDate = isReadOnlyExtend
            ? row.cancellationExtensionDate
            : row.extendedExecutionDate;

          const current =
            rawDate && dayjs(rawDate).isValid() ? dayjs(rawDate) : null;

          const dueIn = Number(row?.dueIn ?? 0);
          const disabledBecauseDueIn = dueIn === 0;
          const disabled = disabledBecauseDueIn || isReadOnlyExtend;

          const tooltipTitle = disabledBecauseDueIn
            ? "The cancellation execution date cannot be extended as the subscription is scheduled to cancel today. Please contact the Billing team for assistance."
            : "";

          return (
            <Tooltip
              title={tooltipTitle}
              disableHoverListener={!tooltipTitle}
            >
              <span>
                <DatePicker
                  value={current}
                  onChange={(val) => {
                    if (disabled || !onExtendedDateChange) return;
                    onExtendedDateChange(row.id, val);
                  }}
                  minDate={min || undefined}
                  maxDate={max || undefined}
                  format={DATE_FORMAT}
                  disabled={disabled}
                  slotProps={{
                    textField: {
                      variant: "outlined",
                      margin: "dense",
                      size: "small",
                      fullWidth: true,
                      InputLabelProps: { shrink: true },
                      inputProps: disabled ? { readOnly: true } : {},
                    },
                  }}
                />
              </span>
            </Tooltip>
          );
        },
      },
    ];
  }
  const isNonPaymentDirect =
    isSelectedActionTypeNonPaymentDirect(selectedActionType);
  const isNonPaymentIndirect =
    isSelectedActionTypeNonPaymentIndirect(selectedActionType);
  const isBizOperationDirect =
    isSelectedActionTypeBizOperationDirect(selectedActionType);

  return [
    {
      field: "accountNumber",
      headerName: "Account Number",
      width: 145,
      headerAlign: "center",
      align: "left",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <HyperlinkCell
          linkText={params?.row?.accountNumber}
          linkUrl={params?.row?.accountUrl}
        />
      ),
    },
    {
      field: "name",
      headerName: "Account Name",
      width: 180,
      headerAlign: "center",
      align: "left",
      sortable: false,
      filterable: false,
    },
    {
      field: "dueIn",
      headerName: "Due for Cancel (days)",
      width: 120,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      cellClassName: "age-grid-button",
      renderHeader: (params) => (
        <HeaderWithFooterCell
          header={"Due for Cancel"}
          footer={"in days"}
          tooltip={"Due for Cancel"}
        />
      ),
      renderCell: (params) => <AgeCell value={params?.row?.dueIn} />,
    },
    ...(isNonPaymentDirect
      ? [
          {
            field: "promisedPayDate",
            headerName: "Resolution Date",
            width: 180,
            headerAlign: "center",
            align: "center",
            sortable: false,
            filterable: false,
            editable: false,
            cellClassName: "age-grid-button",
          renderHeader: (params) => (
              <HeaderWithFooterCell
                header={"Resolution Date"}
                footer={DATE_FORMAT}
                tooltip={"Resolution Date"}
              />
            ),
            renderCell: (params) => {
              const p2pDate = params?.row?.promisedPayDate || "";
              if (isApproveAction && !p2pDate.length) return <></>;

              return p2pDate ? (
                <Tooltip title={p2pDate}>
                  <Box>
                    <label>{p2pDate}</label>
                  </Box>
                </Tooltip>
              ) : (
              <>
                <Tooltip title="Please select Resolution Date">
                  <HelpOutlineIcon
                    fontSize="small"
                    sx={{
                      "&:hover": {
                        color: "#e65100",
                      },
                    }}
                  />
                </Tooltip>
              </>
              );
            },
          },
        ]
      : []),
    {
      field: "zoomAccountNumber",
      headerName: "ZAN",
      description: "Zoom Account Number",
      width: 150,
      headerAlign: "center",
      align: "right",
      sortable: false,
      filterable: false,
      cellClassName: "age-grid-button",
      renderHeader: (params) => (
        <HeaderWithFooterCell
          header={"Zoom Account Number"}
          tooltip={"Zoom Account Number"}
        />
      ),
    },
    ...(isNonPaymentIndirect
      ? [
          {
            field: "affectedAccountCount",
            headerName: "Affected Account Count",
            description: "Affected Account Count",
            width: 140,
            align: "right",
            headerAlign: "center",
            filterable: false,
            cellClassName: "age-grid-button",
          renderHeader: (params) => (
              <HeaderWithFooterCell
                header={"Affected Account Count"}
                tooltip={"Affected Account Count"}
              />
            ),
          },
        ]
      : []),
    {
      field: "mrrUSD",
      headerName: "Committed MRR",
      description: "Committed MRR",
      width: 100,
      headerAlign: "center",
      align: "right",
      sortable: false,
      filterable: false,
      renderHeader: (params) => (
        <HeaderWithFooterCell
          header={"Committed MRR"}
          tooltip={"Committed MRR"}
        />
      ),
      renderCell: (params) => (
        <BalanceCell
          balance={params?.row?.subCmrr}
          balanceUSD={params?.row?.mrrUSD}
          currency={params?.row?.currency || ""}
        />
      ),
    },
    {
      field: "accountBalanceUSD",
      headerName: "Balance",
      width: 100,
      headerAlign: "center",
      align: "right",
      sortable: false,
      filterable: false,
      renderHeader: (params) => (
        <Tooltip title="Balance">
          <div className="table-header-amount">
            <strong className="table-header-ellipsis">Balance</strong>
          </div>
        </Tooltip>
      ),
      renderCell: (params) => (
        <BalanceCell
          balance={params?.row?.accountBalance}
          balanceUSD={params?.row?.accountBalanceUSD}
          currency={params?.row?.currency || ""}
        />
      ),
    },
    {
      field: "currency",
      headerName: "Currency",
      width: 80,
      headerAlign: "center",
      align: "left",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <CurrencyCell localCurrency={params?.row?.currency || ""} />
      ),
    },
    {
      field: "subOriginalCreatedDate",
      headerName: "Subscription Creation Date",
      width: 150,
      headerAlign: "center",
      align: "left",
      sortable: false,
      filterable: false,
      cellClassName: "age-grid-button",
      renderHeader: (params) => (
        <HeaderWithFooterCell
          header={"Subscription Creation Date"}
          footer={DATE_FORMAT}
          tooltip={"Subscription Creation Date"}
        />
      ),
    },
    {
      field: "businessComment",
      headerName: "Business Reason",
      width: 200,
      headerAlign: "center",
      align: "left",
      sortable: false,
      filterable: false,
    },
    ...(isNonPaymentDirect || isBizOperationDirect
      ? [
          {
            field: "detailedInfo",
            headerName: "Detailed Info",
            width: 150,
            align: "left",
            headerAlign: "center",
            filterable: false,
            sortable: false,
            cellClassName: "age-grid-button",
          },
        ]
      : []),
  ];
};

export const columnsProductCatalogue = [
  {
    field: "ratePlanChargeId",
    headerName: "Rate Plan Charge ID",
    width: 160,
    headerAlign: "center",
    align: "left",
    sortable: false,
    filterable: false,
    renderHeader: (params) => (
      <HeaderWithFooterCell
        header={"Rate Plan Charge ID"}
        tooltip={"Rate Plan Charge ID"}
      />
    ),
  },
  {
    field: "accountId",
    headerName: "Account ID",
    width: 180,
    headerAlign: "center",
    align: "left",
    sortable: false,
    filterable: false,
  },
  {
    field: "status",
    headerName: "Status",
    width: 110,
    headerAlign: "center",
    align: "center",
    sortable: false,
    filterable: false,
    cellClassName: "age-grid-button",
    renderCell: (params) => getProductCatalogueStatus(params?.row?.status),
  },
  {
    field: "createdDate",
    headerName: "Creation Date",
    width: 160,
    align: "center",
    headerAlign: "center",
    filterable: false,
    sortable: false,
    cellClassName: "age-grid-button",
    renderHeader: (params) => (
      <HeaderWithFooterCell
        header={"Creation Date"}
        footer={DATE_FORMAT}
        tooltip={"Creation Date"}
      />
    ),
    renderCell: (params) =>
      moment(params?.row?.createdDate).format(DATE_FORMAT),
  },
  {
    field: "modifiedDate",
    headerName: "Modification Date",
    width: 160,
    align: "center",
    headerAlign: "center",
    filterable: false,
    sortable: false,
    cellClassName: "age-grid-button",
    renderHeader: (params) => (
      <HeaderWithFooterCell
        header={"Modification Date"}
        footer={DATE_FORMAT}
        tooltip={"Modification Date"}
      />
    ),
    renderCell: (params) =>
      moment(params?.row?.modifiedDate).format(DATE_FORMAT),
  },
  {
    field: "approverName",
    headerName: "Approver Name",
    description: "Approver Name",
    width: 160,
    headerAlign: "center",
    align: "right",
    sortable: false,
    filterable: false,
    cellClassName: "age-grid-button",
  },
  {
    field: "approverEmail",
    headerName: "Approver Email",
    description: "Approver Email",
    width: 180,
    headerAlign: "center",
    align: "right",
    sortable: false,
    filterable: false,
  },
  {
    field: "actionTime",
    headerName: "Action Time",
    description: "Action Time",
    width: 160,
    headerAlign: "center",
    align: "right",
    sortable: false,
    filterable: false,
  },
];

export const productCatalogActionColumns = [
  {
    field: "ratePlanChargeId",
    headerName: "Rate Plan Charge ID",
    flex: 0.3,
  },
  {
    field: "failureReason",
    headerName: "Failure Reason",
    flex: 0.7,
  },
];

const AgeCell = ({ value }) => {
  const getStyle = () => {
    if (value <= 10) {
      return { backgroundColor: "#a42900", color: "#ffffffff" };
    } else if (value > 10 && value <= 20) {
      return { backgroundColor: "#cd6f00", color: "#ffffffff" };
    } else if (value > 0) {
      return { backgroundColor: "#fde68a", color: "#544301ff" };
    }
    return {};
  };
  return (
    <span className="cell_custom_badge" style={getStyle()}>
      {value}
    </span>
  );
};

const ApprovalStatus = ({
  names,
  status,
  approvalRejectionDate,
  promisedPayDate,
  showPromisedPayDate,
}) => {
  const displayName = names?.[0] || "";
  const totalCount = names?.length || 1;
  const remainingCount = totalCount - 1;
  const remainingNames = names?.slice(1, totalCount) || [];
  const isPendingApproval =
    status === "Pending" || status === "" || !status?.length;
  const isRejected = status === "Rejected";
  const isApproved = status === "Approved" || status === "Auto Approval";
  const isListEmpty =
    displayName === "N/A" || displayName === "" || !displayName?.length;
  return !isListEmpty ? (
    <Box sx={{ height: "62px", alignContent: "flex-start" }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Tooltip title={displayName}>
          <Typography
            className={classNames(
              "approver-cell",
              isRejected
                ? "rejected_name"
                : isPendingApproval
                ? "pending_name"
                : "name"
            )}
          >
            {displayName}
          </Typography>
        </Tooltip>
        <Tooltip
          title={
            <>
              {remainingNames.map((name, index) => (
                <Typography key={index} variant="body2">
                  {name}
                </Typography>
              ))}
            </>
          }
          placement="right"
          style={{ display: "flex", marginTop: "5px" }}
        >
          {remainingCount > 0 ? (
            <div>
              <h5 style={{ margin: 0, lineHeight: 1 }}>{remainingCount}</h5>
              <PersonAddIcon
                fontSize="small"
                sx={{
                  "&:hover": {
                    color: "#e65100",
                  },
                }}
              />
            </div>
          ) : (
            <></>
          )}
        </Tooltip>
      </div>
      <Typography
        className={
          isRejected
            ? "rejected_status"
            : isPendingApproval
            ? "pending_status"
            : "status"
        }
      >
        {isPendingApproval ? "Pending Approval" : ""}
      </Typography>
      {isRejected ? (
        <>
          <Typography className="rejected_date">
            Declined on: {moment(approvalRejectionDate).format(DATE_FORMAT)}
          </Typography>
          {showPromisedPayDate && promisedPayDate ? (
            <Typography className="rejected_date">
              Resolution Date: {moment(promisedPayDate).format(DATE_FORMAT)}
            </Typography>
          ) : (
            <></>
          )}
        </>
      ) : isApproved ? (
        <>
          <Typography className="date">
            Approved on: {moment(approvalRejectionDate).format(DATE_FORMAT)}
          </Typography>
        </>
      ) : (
        <></>
      )}
    </Box>
  ) : (
    <></>
  );
};

const ApprovalStatusHistorical = ({
  names,
  status,
  approvalRejectionDate,
  promisedPayDate,
  showPromisedPayDate,
}) => {
  const displayName = names?.[0] || "";
  const totalCount = names?.length || 1;
  const remainingCount = totalCount - 1;
  const remainingNames = names?.slice(1, totalCount) || [];
  const isRejected = status === "Rejected";
  const isApproved = status === "Approved" || status === "Auto Approval";
  const isListEmpty =
    displayName === "N/A" || displayName === "" || !displayName?.length;
  return !isListEmpty ? (
    <Box sx={{ height: "62px", alignContent: "flex-start" }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Tooltip title={displayName}>
          <Typography
            className={classNames(
              "approver-cell",
              isRejected ? "rejected_name" : "name"
            )}
          >
            {displayName}
          </Typography>
        </Tooltip>
        <Tooltip
          title={
            <>
              {remainingNames.map((name, index) => (
                <Typography key={index} variant="body2">
                  {name}
                </Typography>
              ))}
            </>
          }
          placement="right"
          style={{ display: "flex", marginTop: "5px" }}
        >
          {remainingCount > 0 ? (
            <div>
              <h5 style={{ margin: 0, lineHeight: 1 }}>{remainingCount}</h5>
              <PersonAddIcon
                fontSize="small"
                sx={{
                  "&:hover": {
                    color: "#e65100",
                  },
                }}
              />
            </div>
          ) : (
            <></>
          )}
        </Tooltip>
      </div>
      {isRejected ? (
        <>
          <Typography className="rejected_date">
            Declined on: {moment(approvalRejectionDate).format(DATE_FORMAT)}
          </Typography>
          {showPromisedPayDate && promisedPayDate ? (
            <Typography className="rejected_date">
              Resolution Date: {moment(promisedPayDate).format(DATE_FORMAT)}
            </Typography>
          ) : (
            <></>
          )}
        </>
      ) : isApproved ? (
        <>
          <Typography className="date">
            Approved on: {moment(approvalRejectionDate).format(DATE_FORMAT)}
          </Typography>
        </>
      ) : (
        <></>
      )}
    </Box>
  ) : (
    <></>
  );
};

const BalanceCell = ({ balance, balanceUSD, currency }) => {
  if (isCurrencyNotAvailable(currency)) {
    return (
      <Typography
        style={{
          textAlign: "end",
          color: "#808080",
          fontSize: "0.875rem",
          lineHeight: "unset",
        }}
      >
        Not Available
      </Typography>
    );
  }

  const isLocalUSD = currency === "USD";
  return (
    <Box sx={{ height: "62px", alignContent: "center" }}>
      {!isLocalUSD ? (
        <Typography
          style={{ fontWeight: "bold", textAlign: "end", fontSize: "0.875rem" }}
        >
          {getFormattedAmount(balanceUSD)}
        </Typography>
      ) : (
        <Typography
          style={{ fontWeight: "bold", textAlign: "end", fontSize: "0.875rem" }}
        >
          {isNonDecimalCurrency(currency)
            ? getFormattedAmountNonDecimalCurrency(balance)
            : getFormattedAmount(balance)}
        </Typography>
      )}
      {!isLocalUSD ? (
        <Typography
          style={{ textAlign: "end", color: "#808080", fontSize: "0.875rem" }}
        >
          {isNonDecimalCurrency(currency)
            ? getFormattedAmountNonDecimalCurrency(balance)
            : getFormattedAmount(balance)}
        </Typography>
      ) : (
        <></>
      )}
    </Box>
  );
};

const DeferCancellationHistory = ({ audits }) => {
  const [open, setOpen] = React.useState(false);

  // sanitize
  const raw = Array.isArray(audits) ? audits.filter(Boolean) : [];
  if (!raw.length) return <></>;

  const items = React.useMemo(
    () => [...raw].sort((a, b) => dayjs(b?.lastUpdatedAt).valueOf() - dayjs(a?.lastUpdatedAt).valueOf()),
    [raw]
  );

  const fmt = (iso) => (iso && dayjs(iso).isValid() ? dayjs(iso).format(DATE_FORMAT) : "");

  const RecordBlock = ({ rec, divider }) => {
    const proposed = fmt(rec?.currentValues?.executionDate);
    const current  = fmt(rec?.previousValues?.executionDate);
    const comments = (rec?.comments ?? "").trim();
    return (
      <Box sx={{ mb: divider ? 1 : 0 }}>
        <Typography className="date">Current Execution Date: {current}</Typography>
        <Typography className="date">Proposed Date: {proposed}</Typography>
        <Typography
          className="date"
          sx={{
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >
          Comments: {comments}
        </Typography>

        {divider && <Divider sx={{ mt: 1 }} />}
      </Box>
    );
  };

  const LONG_COMMENT_THRESHOLD = 80;

  // ---------- SINGLE RECORD ----------
  if (items.length === 1) {
    const rec = items[0];
    const comments = (rec?.comments ?? "").trim();
    const isLong = comments.length > LONG_COMMENT_THRESHOLD;

    if (!isLong) {
      // previous behavior
      return (
        <Box sx={{ width: "100%", py: 0.5 }}>
          <RecordBlock rec={rec} />
        </Box>
      );
    }

    const proposed = fmt(rec?.currentValues?.executionDate);
    const current  = fmt(rec?.previousValues?.executionDate);

    return (
      <>
        <Box sx={{ width: "100%", py: 0.5 }}>
          <Typography className="date">Current Execution Date: {current}</Typography>
          <Typography className="date">Proposed Date: {proposed}</Typography>

          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
            <Typography
              className="date"
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              Comments: {comments}
            </Typography>
            <Link
              component="button"
              onClick={() => setOpen(true)}
              underline="hover"
              sx={{ fontSize: 12, lineHeight: 1.2, whiteSpace: "nowrap" }}
            >
              Show more
            </Link>
          </Box>
        </Box>

        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pr: 2,
              fontWeight: 600,
            }}
          >
            Defer Cancellation
            <IconButton
              onClick={() => setOpen(false)}
              sx={{ color: "text.secondary" }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            <RecordBlock rec={rec} />
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // ---------- MULTIPLE RECORDS ----------
  const first = items[0];
  const remaining = items.length - 1;
  const firstProposed = fmt(first?.currentValues?.executionDate);
  const firstCurrent  = fmt(first?.previousValues?.executionDate);
  const firstComments = (first?.comments ?? "").trim();

  return (
    <>
      <Box sx={{ width: "100%", py: 0.5 }}>
        <Typography className="date">Current Execution Date: {firstCurrent}</Typography>
        <Typography className="date">Proposed Date: {firstProposed}</Typography>

        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
          <Typography
            className="date"
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Comments: {firstComments}
          </Typography>
          <Link
            component="button"
            onClick={() => setOpen(true)}
            underline="hover"
            sx={{ fontSize: 12, lineHeight: 1.2, whiteSpace: "nowrap" }}
            title={`Show ${remaining} more record${remaining > 1 ? "s" : ""}`}
          >
            Show more ({remaining})
          </Link>
        </Box>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 2,
            fontWeight: 600,
          }}
        >
          Defer Cancellation
          <IconButton
            onClick={() => setOpen(false)}
            sx={{
              color: "text.secondary",
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {items.map((a, idx) => (
            <RecordBlock key={idx} rec={a} divider={idx < items.length - 1} />
          ))}
        </DialogContent>
        
      </Dialog>
    </>
  );
};







const CurrencyCell = ({ localCurrency }) => {
  if (isCurrencyNotAvailable(localCurrency)) {
    return (
      <Typography
        style={{
          textAlign: "end",
          color: "#808080",
          fontSize: "0.875rem",
          lineHeight: "unset",
        }}
      >
        Not Available
      </Typography>
    );
  }

  const isLocalUSD = localCurrency === "USD";
  return (
    <Box sx={{ height: "62px", alignContent: "center" }}>
      <Typography
        style={{ fontWeight: "bold", textAlign: "center", fontSize: "0.875rem" }}
      >
        USD
      </Typography>
      {!isLocalUSD ? (
        <Typography
          style={{ textAlign: "center", color: "#808080", fontSize: "0.875rem" }}
        >{`(${localCurrency})`}</Typography>
      ) : (
        <></>
      )}
    </Box>
  );
};

const ErrorMessageCell = ({ value }) => {
  if (!value) {
    return <></>;
  }

  return (
    <Tooltip title={value} placement="top-start">
      <Typography
        sx={{
          width: "100%",
          fontSize: "0.875rem",
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "normal",
          wordBreak: "break-word",
          textAlign: "left",
        }}
      >
        {value}
      </Typography>
    </Tooltip>
  );
};

const HeaderWithFooterCell = ({ header, footer, tooltip }) => (
  <Tooltip title={tooltip}>
    <Box sx={{ display: "grid", marginTop: "22.5px" }}>
      <strong className="table-header-ellipsis">{header}</strong>
      {footer ? (
        <Box
          sx={{
            display: "block",
            color: "#808080",
            fontSize: "0.875rem",
            fontWeight: 500,
            textAlign: "center",
          }}
        >
          {footer}
        </Box>
      ) : (
        <></>
      )}
    </Box>
  </Tooltip>
);

/**
 * Cancellation Reason cell. For TNS the tooltip explains what the reason code means
 * (see TNS_REASON_CODE_DESCRIPTIONS); when there is no mapping — or for non-TNS action
 * types — it falls back to the raw value, preserving the previous behaviour.
 */
const CancellationReasonCell = ({ value, isTNS }) => {
  const description = isTNS ? getTnsReasonDescription(value) : "";
  const tooltip = description || value || "";

  return (
    <Tooltip title={tooltip} arrow placement="top">
      <label style={{ cursor: description ? "help" : "inherit" }}>{value}</label>
    </Tooltip>
  );
};

/**
 * Small copy-to-clipboard icon button. Shows a transient check mark on success.
 * Uses the async Clipboard API with a textarea+execCommand fallback for non-secure
 * origins (http dev hosts), where navigator.clipboard is undefined.
 */
const CopyIconButton = ({ value, label = "value" }) => {
  const [copied, setCopied] = useState(false);

  if (value === null || value === undefined || value === "") return null;

  const text = String(value);

  const fallbackCopy = () => {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      return true;
    } catch (e) {
      return false;
    }
  };

  const markCopied = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleCopy = (event) => {
    // The cell may sit inside a hyperlink / selectable row - don't trigger those.
    event.stopPropagation();
    event.preventDefault();

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(markCopied)
        .catch(() => {
          if (fallbackCopy()) markCopied();
        });
    } else if (fallbackCopy()) {
      markCopied();
    }
  };

  return (
    <Tooltip title={copied ? "Copied!" : `Copy ${label}`}>
      <IconButton
        size="small"
        aria-label={`Copy ${label}`}
        onClick={handleCopy}
        sx={{
          padding: "2px",
          marginLeft: "2px",
          color: copied ? "success.main" : "action.active",
        }}
      >
        {copied ? (
          <CheckIcon sx={{ fontSize: "14px" }} />
        ) : (
          <ContentCopyIcon sx={{ fontSize: "14px" }} />
        )}
      </IconButton>
    </Tooltip>
  );
};

const HyperlinkCell = ({ linkText, linkUrl, copyable = false, copyLabel }) => (
  <Box
    sx={{
      height: "62px",
      display: "flex",
      alignItems: "center",
      gap: "2px",
      minWidth: 0,
    }}
  >
    <Link
      href={linkUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="cancellation-table-hyperlink"
      sx={{
        display: "flex",
        alignItems: "center",
        textAlign: "start",
        textDecoration: "none",
        color: "primary.main",
        "&:hover": { textDecoration: "underline" },
      }}
    >
      {linkText}
    </Link>
    {copyable ? (
      <CopyIconButton value={linkText} label={copyLabel || "value"} />
    ) : null}
  </Box>
);

/** Plain text cell with a copy-to-clipboard icon. */
const CopyableTextCell = ({ value, label, align = "flex-start" }) => (
  <Box
    sx={{
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: align,
      gap: "2px",
      minWidth: 0,
    }}
  >
    <span
      style={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </span>
    <CopyIconButton value={value} label={label} />
  </Box>
);
